import { debounce } from "lodash";
import GeocoderResultDecorator from "./GeocoderResultDecorator";

const defaultOptions = {
  debounceTimeout: 200,
  debounceLeading: false,
  debounceTrailing: true,
};

/**
 * A wrapper for google.maps.geocoder and also a port of some the tourbuilder project files.
 */
class Geocoder {
  /**
   * @property {google.maps.Geocoder}
   */
  geocoder;

  constructor(optionsOverrides = {}) {
    if (!(window.google && window.google.maps)) {
      throw new Error("Google maps library not detected");
    }

    this.geocoder = new window.google.maps.Geocoder();
    const options = { ...defaultOptions, ...optionsOverrides };

    this.geocode = debounce(this.geocode, options.debounceTimeout, {
      leading: options.debounceLeading,
      trailing: options.debounceTrailing,
    });
  }

  geocode(request, callback) {
    this.geocoder.geocode(request, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK) {
        callback([]);
        return;
      }

      const normalized = [];

      for (let i = 0; i < results.length; i += 1) {
        const result = results[i];
        const decorator = new GeocoderResultDecorator(result);

        if (decorator.neighborhood !== "") {
          // deep cloning
          const cityResult = JSON.parse(JSON.stringify(result));
          const neighborhoodResult = JSON.parse(JSON.stringify(result));

          // make `result` internally consistent (ie make the formatted_address match the "city" name)
          cityResult.formatted_address = decorator.getFullAddress();

          // fake out an alt name
          const neighborhoodDecorator = new GeocoderResultDecorator(neighborhoodResult);
          neighborhoodDecorator.replaceCityWithNeighborhood();

          // generate the formatted address when you use the neighborhood for the city
          neighborhoodResult.formatted_address = neighborhoodDecorator.getFullAddress();

          // display the address with city result first
          normalized.push(cityResult);

          // if our alt/neighborhood name is different, offer it as a choice as well
          if (neighborhoodResult.formatted_address !== cityResult.formatted_address) {
            normalized.push(neighborhoodResult);
          }
        } else {
          normalized.push(result);
        }
      }

      const unique = {};

      for (let i = 0; i < normalized.length; i += 1) {
        const result = normalized[i];

        if (!unique[result.formatted_address]) {
          unique[result.formatted_address] = result;
        }
      }

      const flatArray = Object.keys(unique).map(key => unique[key]);

      callback(flatArray);
    });
  }
}

export default Geocoder;
